import React, { useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';

/**
 * Mr. Ludo Arena — wraps the standalone HTML game in a sandboxed iframe.
 * Communicates with the parent via postMessage:
 *   parent -> iframe:  { type: 'DOMIA_INIT', balance, avatar, ... }
 *   parent -> iframe:  { type: 'DOMIA_BALANCE_UPDATE', balance }
 *   iframe -> parent:  { type: 'LUDO_EXIT' }
 *   iframe -> parent:  { type: 'DOMIA_BALANCE_UPDATE', balance } (from in-game settle)
 */
const LudoGame = ({ onClose }) => {
  const { user, refresh } = useAuth();
  const iframeRef = useRef(null);

  useEffect(() => {
    const handleMessage = (e) => {
      const t = (e.data || {}).type;
      if (t === 'LUDO_EXIT') onClose && onClose();
      // iframe → parent: balance changed (settle). Refresh React user state.
      if (t === 'DOMIA_BALANCE_UPDATE') {
        try { refresh(); } catch (_) { /* ignore */ }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose, refresh]);

  // Push live balance from React → iframe whenever React user.walletBalance changes
  useEffect(() => {
    const bal = Number(user?.walletBalance || 0);
    iframeRef.current?.contentWindow?.postMessage({ type: 'DOMIA_BALANCE_UPDATE', balance: bal }, '*');
  }, [user?.walletBalance]);

  const onLoad = () => {
    try {
      const token = localStorage.getItem('da_token') || '';
      iframeRef.current?.contentWindow?.postMessage({
        type: 'DOMIA_INIT',
        balance: Number(user?.walletBalance || 0),
        avatar: user?.avatar || '🦁',
        username: user?.username || 'Player',
        backendUrl: process.env.REACT_APP_BACKEND_URL,
        token,
      }, '*');
    } catch (_) { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950" data-testid="ludo-game-container">
      <iframe
        ref={iframeRef}
        src="/ludo.html"
        title="Mr. Ludo Arena"
        onLoad={onLoad}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-popups"
        data-testid="ludo-iframe"
      />
    </div>
  );
};

export default LudoGame;
